import React, { ReactElement, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { FormattedMessage } from 'intl';
import { WorkflowTranslationKeys } from 'intl/keys';

import { compareObjectIds } from 'shared/utils/id/compare';
import { StageRow } from '../StageRow';
import type { StageListPanelProps } from './types';
import { rootSx, emptyStateSx, emptyStateTextSx, footerSx } from './styles';

export type { StageListPanelProps } from './types';

export function StageListPanel({
  stages,
  isLocked,
  disabled,
  approversDisabled,
  showNameErrors,
  availableApprovers,
  onStagesReorder,
  onAddStage,
  onUpdateStage,
  onDeleteStage,
}: StageListPanelProps): ReactElement {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const stageLocalIds = useMemo(() => stages.map((s) => s._localId), [stages]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const oldIndex = stages.findIndex((s) => compareObjectIds(s._localId, active.id));
      const newIndex = stages.findIndex((s) => compareObjectIds(s._localId, over.id));

      if (oldIndex === -1 || newIndex === -1) return;

      const lastIndex = stages.length - 1;
      if (oldIndex === 0 || oldIndex === lastIndex) return;
      if (newIndex === 0 || newIndex === lastIndex) return;

      onStagesReorder(arrayMove(stages, oldIndex, newIndex));
    },
    [stages, onStagesReorder]
  );

  const totalStages = stages.length;

  return (
    <Box sx={rootSx}>
      {stages.length === 0 ? (
        <Box sx={emptyStateSx}>
          <Typography variant="body2" sx={emptyStateTextSx}>
            <FormattedMessage id={WorkflowTranslationKeys.configurationEmptyState} />
          </Typography>
        </Box>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={stageLocalIds}
            strategy={verticalListSortingStrategy}
          >
            {stages.map((stage, index) => (
              <StageRow
                key={stage._localId}
                stage={stage}
                stageIndex={index}
                totalStages={totalStages}
                isLocked={isLocked}
                disabled={disabled}
                approversDisabled={approversDisabled}
                focusNameInput={index === stages.length - 1 && !stage.name.trim()}
                showNameError={showNameErrors}
                availableApprovers={availableApprovers}
                onUpdate={onUpdateStage}
                onDelete={() => onDeleteStage(stage._localId)}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}

      {!isLocked ? (
        <Box sx={footerSx}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={onAddStage}
            disabled={disabled}
            data-qa="stage-list-add-button"
          >
            <FormattedMessage id={WorkflowTranslationKeys.creatorAddStage} />
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}
